'use client'
import Footer from '@/components/Footer';
import usePhoneNumber from '@/hooks/usePhoneNumber';
import 'swiper/css';
import 'swiper/css/pagination';
import MainMenu from '@/components/MainMenu';
import Image from 'next/image';
import Link from 'next/link';
import { use, useCallback, useEffect, useState } from 'react';
import { sendRequestNoToken } from '@/utils/apiService';
import { SpinningSkeletonLoader } from '@/components/skeleton/SpinningSkeleton';
import { Article, formatDateTime } from '@/utils/types';
import Pagination from '@/components/Pagination';
import { prependCoreUrl } from '@/utils/globals';
import { Fragment } from 'react';

interface School {
    id: number;
    name: string;
    one_credit: number;
    all_credit: number;
    image: string;
    text: string;
    courses: Course[];
    short_description: string;
}

interface Course {
    id: number;
    name: string;
    primary_subject_choices: string[];
    primary_subject_point: number;
    secondary_subject_choices: string[];
    secondary_subject_point: number;
    study_year: number;
}

const Page = ({ params }: { params: Promise<{ pk: string }> }) => {
    const { pk } = use(params);
    const { phoneNumber, isCookieLoading } = usePhoneNumber();
    const [isLoading, setIsLoading] = useState(true);
    const [result, setResult] = useState<School | null>(null);
    const [isError, setIsError] = useState(false);
    const [filter, setFilter] = useState(""); // State for filter input

    useEffect(() => {
        sendRequestNoToken("GET", `/api/schools/${pk}`, null)
            .then((data) => {
                setResult(data);
            })
            .catch((error) => {
                setIsError(true);
                console.error("Error fetching data:", error);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [pk]);

    if (isError) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 dark:bg-gray-900">
                <h1 className="text-6xl font-bold text-purple-600 mb-4">404</h1>
                <p className="text-xl text-gray-800 dark:text-gray-200 mb-8">
                    Таны хандсан хуудас олдсонгүй!
                </p>
                <Link href="/">
                    <button className="bg-purple-600 text-white px-6 py-3 rounded-md text-lg font-semibold hover:bg-purple-700">
                        Нүүр хуудас
                    </button>
                </Link>
            </div>
        );
    }

    if (result === null) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-100 dark:bg-dark-800">
                <div className="text-center">
                    <div className="spinner-border animate-spin inline-block w-16 h-16 border-8 border-solid border-purple-500 border-t-transparent rounded-full"></div>
                    <p className="mt-4 text-xl dark:text-dark-100 text-gray-700">Уншиж байна...</p>
                </div>
            </div>
        );
    }

    // Filter function to match course names with the filter input
    const filteredCourses = result.courses.filter(course =>
        course.name.toLowerCase().includes(filter.toLowerCase())
    );

    return (
        <main className="flex flex-col items-center justify-between overflow-hidden">
            <MainMenu isLoading={isCookieLoading} phoneNumber={phoneNumber} />
            <section className="text-start pt-10 pb-0 md:pt-24 w-screen justify-start bg-gradient-to-b from-gradient-start to-white dark:from-purple-700 dark:to-dark-800">
                <div className='container mt-20 mb-4 text-start'>
                    <div className='flex flex-row space-x-4 items-center'>
                        <div className='flex-shrink-0 h-[80px] w-[80px] bg-gray-400 rounded overflow-hidden'>
                            <Image src={result.image} width={80} height={80} alt={result.name} />
                        </div>
                        <div className='font-semibold md:text-4xl text-lg'>{result.name}</div>
                    </div>
                </div>
            </section>

            <section className='dark:bg-dark-800 w-full'>
                <div className='min-h-[50vh] w-full pb-10 container'>
                    <div className='flex flex-col space-y-8 md:space-y-4'>
                        <div className='mt-4 text-md overflow-hidden' dangerouslySetInnerHTML={{ __html: prependCoreUrl(result.text) || "" }}></div>
                    </div>
                    <div className='mt-2'>
                        {result.one_credit != 0 && (
                            <div>
                                Нэг кредитийн төлбөр: {new Intl.NumberFormat('mn-MN').format(result.one_credit)} ₮
                            </div>
                        )}
                    </div>
                    <div>
                        {result.one_credit != 0 && (
                            <div>
                                Сургалтын төлбөр: {new Intl.NumberFormat('mn-MN').format(result.one_credit)} ₮
                            </div>
                        )}
                    </div>

                    <div className='font-semibold text-lg mt-6 mb-3'>
                        Хөтөлбөрүүд:
                    </div>

                    {/* Filter Input */}
                    <input
                        type="text"
                        placeholder="Хөтөлбөр хайх..."
                        className="border border-dark-100 rounded-md px-4 py-2 w-full md:w-1/4 text-sm placeholder:text-dark-300 dark:text-dark-700"
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                    />

                    <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4'>
                        {filteredCourses.map((item, index) => {
                            const maxLength = Math.max(item.primary_subject_choices.length, item.secondary_subject_choices.length);
                            const paddedPrimarySubjects = [...item.primary_subject_choices];
                            const paddedSecondarySubjects = [...item.secondary_subject_choices];

                            while (paddedPrimarySubjects.length < maxLength) {
                                paddedPrimarySubjects.push("");
                            }

                            while (paddedSecondarySubjects.length < maxLength) {
                                paddedSecondarySubjects.push("");
                            }

                            return (
                                <div key={index} className="bg-white shadow-md rounded-lg p-4 border border-purple-100 dark:bg-dark-700">
                                    <h3 className="text-lg font-semibold">{item.name}</h3>
                                    <p className="text-md text-dark-500 text-sm dark:text-dark-100">Суралцах жил: {item.study_year}</p>
                                    <div className="mt-2">
                                        {paddedPrimarySubjects.some(subject => subject) && (
                                            <Fragment>
                                                <h4 className="font-semibold mt-2 text-center">ЭЕШ Сонголт 1</h4>
                                                <table className="w-full border-collapse mt-1">
                                                    <thead>
                                                        <tr>
                                                            {paddedPrimarySubjects.map((subject, idx) => (
                                                                subject && (
                                                                    <td key={idx} className="border px-2 py-1 text-sm text-center">
                                                                        {subject}
                                                                    </td>
                                                                )
                                                            ))}
                                                            <td className="border px-2 py-1 text-sm text-center">
                                                                {item.primary_subject_point} оноо
                                                            </td>
                                                        </tr>
                                                    </thead>
                                                </table>
                                            </Fragment>
                                        )}
                                        {paddedSecondarySubjects.some(subject => subject) && (
                                            <Fragment>
                                                <h4 className="font-semibold mt-4 text-center">ЭЕШ Сонголт 2</h4>
                                                <table className="w-full border-collapse mt-1">
                                                    <tbody>
                                                        <tr>
                                                            {paddedSecondarySubjects.map((subject, idx) => (
                                                                subject && (
                                                                    <td key={idx} className="border px-2 py-1 text-sm text-center">
                                                                        {subject}
                                                                    </td>
                                                                )
                                                            ))}
                                                            <td className="border px-2 py-1 text-sm text-center">
                                                                {item.secondary_subject_point} оноо
                                                            </td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </Fragment>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            <Footer />
        </main>
    );
};

export default Page;
